import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { agenciesAPI, plansAPI } from '../../services/api';
import Card from '../common/Card';
import Loading from '../common/Loading';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import AgencyFormModal from './AgencyFormModal';
import { formatDate, handleApiError, showToast, showConfirm } from '../../utils/helpers';

const AgenciesListPage = () => {
  const [agencies, setAgencies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [agenciesResponse, plansResponse] = await Promise.all([
        agenciesAPI.getAll(),
        plansAPI.getAll(),
      ]);

      setAgencies(agenciesResponse.data.agencies || []);
      setPlans(plansResponse.data.plans || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAgency(null);
    setShowModal(true);
  };

  const handleEdit = (agency) => {
    setEditingAgency(agency);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm(
      'Are you sure you want to delete this agency? This action cannot be undone.',
      false
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(id);
      await agenciesAPI.delete(id);
      await fetchData();
      showToast.success('Agency deleted successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingAgency(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    fetchData();
  };

  if (loading) {
    return <Loading fullScreen />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchData} variant="secondary">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agencies</h1>
          <p className="text-gray-600 mt-1">Manage all agencies in the system</p>
        </div>
        <Button variant="primary" onClick={handleCreate}>
          Add New Agency
        </Button>
      </div>

      {/* Agencies Table */}
      {agencies.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-4">No agencies yet</p>
            <Button variant="primary" onClick={handleCreate}>
              Add Your First Agency
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agency Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {agencies.map((agency) => (
                  <tr key={agency.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{agency.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{agency.admin_name}</div>
                      <div className="text-sm text-gray-500">{agency.admin_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{agency.plan_name || 'N/A'}</div>
                      {agency.plan_price !== null && agency.plan_price !== undefined && (
                        <div className="text-sm text-gray-500">
                          ${agency.plan_price === 0 ? 'Free' : agency.plan_price}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          agency.is_active === false
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        {agency.is_active === false ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={agency.subscription_status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(agency.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/super-admin/agencies/${agency.id}`}>
                          <Button variant="secondary" size="sm">
                            View
                          </Button>
                        </Link>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleEdit(agency)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(agency.id)}
                          disabled={deletingId === agency.id}
                        >
                          {deletingId === agency.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Agency Form Modal */}
      {showModal && (
        <AgencyFormModal
          agency={editingAgency}
          plans={plans}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default AgenciesListPage;







